
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/contexts/AuthContext';
import { formatPhoneToE164 } from '@/utils/supabase/courierQueries';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface UserCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function UserCreateDialog({ isOpen, onClose, onSuccess }: UserCreateDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('courier');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!phone) {
        toast.error('Необходимо указать номер телефона');
        setError('Необходимо указать номер телефона');
        setIsLoading(false);
        return;
      }

      if (!password) {
        toast.error('Необходимо указать пароль');
        setError('Необходимо указать пароль');
        setIsLoading(false);
        return;
      }
      
      let formattedPhone: string | null = null;
      
      try {
        formattedPhone = formatPhoneToE164(phone);
        console.log('Форматированный телефон:', formattedPhone);
      } catch (error) {
        toast.error('Неверный формат номера телефона');
        setError('Неверный формат номера телефона');
        setIsLoading(false);
        return;
      }
      
      console.log('Создаем пользователя с данными:', {
        phone: formattedPhone,
        password,
        options: {
          data: {
            name,
            role
          }
        }
      });
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        phone: formattedPhone,
        password,
        options: {
          data: {
            name, // Передаем имя в метаданные
            role
          }
        }
      });
      
      if (authError) {
        console.error('Ошибка при регистрации пользователя:', authError);
        throw authError;
      }

      if (!authData.user) {
        console.error('Пользователь не был создан, authData:', authData);
        throw new Error('Не удалось создать пользователя');
      }
      
      console.log('Пользователь создан успешно, id:', authData.user.id);
      
      // Проверяем, был ли создан профиль автоматически
      console.log('Проверяем наличие профиля для пользователя:', authData.user.id);
      const { data: profileData, error: profileCheckError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();
        
      console.log('Результат проверки профиля:', { profileData, profileCheckError });
        
      if (!profileData || profileCheckError) {
        // Профиль не был создан автоматически, создаем его вручную
        console.log('Профиль не создан, создаем вручную');
        
        const profilePayload = {
          id: authData.user.id,
          name,
          phone: formattedPhone,
          role,
          avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${authData.user.id}`
        };
        
        console.log('Данные для профиля:', profilePayload);
        
        const { error: insertError } = await supabase
          .from('profiles')
          .insert(profilePayload);
          
        if (insertError) {
          console.error('Ошибка при создании профиля:', insertError);
          throw new Error(`Ошибка при создании профиля: ${insertError.message}`);
        }
        
        console.log('Профиль создан успешно');
      }

      toast.success('Пользователь успешно создан');
      onSuccess();
      onClose();
      
      // Сбросить форму
      setName('');
      setPhone('');
      setPassword('');
      setRole('courier');
    } catch (error: any) {
      console.error('Ошибка при создании пользователя:', error);
      
      // Более подробная информация об ошибке
      if (error.code) {
        console.error(`Код ошибки: ${error.code}, сообщение: ${error.message}`);
        
        if (error.code === '23505') {
          setError(`Пользователь с таким номером телефона уже существует`);
          toast.error(`Пользователь с таким номером телефона уже существует`);
        } else if (error.code === 'P0001') {
          setError(`Ошибка триггера базы данных: ${error.message}`);
          toast.error(`Ошибка базы данных: ${error.message}`);
        } else {
          setError(`Ошибка при создании пользователя: ${error.message} (${error.code})`);
          toast.error(`Ошибка при создании пользователя: ${error.message}`);
        }
      } else {
        setError(`Ошибка при создании пользователя: ${error.message || 'Неизвестная ошибка'}`);
        toast.error(`Ошибка при создании пользователя: ${error.message || 'Неизвестная ошибка'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Создание пользователя</DialogTitle>
          <DialogDescription>
            Создайте нового пользователя в системе.
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Имя</Label>
              <Input
                id="name"
                placeholder="Введите имя"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="phone">Телефон</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+7 (999) 123-45-67"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="role">Роль</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите роль" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Администратор</SelectItem>
                  <SelectItem value="courier">Курьер</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Создание...' : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
